import { forwardRef } from 'react';
import type { ChangeEvent, ComponentProps } from 'react';
import MuiTextField from '@mui/material/TextField';
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { IMaskInput } from 'react-imask';

type ItemDeMascaraDinamica = { mask: string | RegExp } & Record<string, unknown>;

export type TextFieldProps = MuiTextFieldProps & {
  /**
   * Descritor de máscara repassado à dependência de formatação. Ausente, o input do MUI é usado
   * sem intermediário. Um array de blocos ({ mask, ...opções }) ativa a máscara dinâmica: a
   * dependência escolhe automaticamente o bloco que melhor encaixa a cada tecla, o que permite o
   * campo crescer de um formato para outro sem depender de estado externo trocar a máscara —
   * troca por comprimento fixo prende o campo no primeiro formato, porque a própria máscara
   * recusa a tecla que faria o estado externo mudar.
   */
  mask?: string | RegExp | ReadonlyArray<ItemDeMascaraDinamica>;
  /**
   * Com máscara, `true` (default) entrega em `onChange` o valor sem os separadores;
   * `false`, o valor exibido. O mesmo vale para o que se informa em `value`.
   * Em campo mascarado, `event.target` é um objeto com `name` e `value`, não o nó do input.
   */
  unmask?: boolean;
  /** Transforma o texto inserido antes de a máscara consumi-lo. Sem `mask`, não tem efeito. */
  transformChar?: (texto: string) => string;
  /** Permite foco e seleção do valor, impede a edição. */
  readOnly?: boolean;
  /**
   * `true` (default) reserva a linha do texto auxiliar mesmo sem mensagem, para o campo não mudar
   * de altura quando um erro aparece. `false` serve a campos que nunca exibem mensagem, como uma
   * busca, onde a linha vazia seria só espaço morto.
   */
  reserveHelperTextSpace?: boolean;
};

type PropsDoInputMascarado = InputBaseComponentProps & {
  mask: string | RegExp | ReadonlyArray<ItemDeMascaraDinamica>;
  unmask?: boolean;
  prepareChar?: (texto: string) => string;
};

// O slot do FormHelperText só existe quando há `helperText`; sem ele, não há como reservar altura
// com `minHeight`. Um espaço não separável dá ao slot vazio a mesma altura de uma linha de
// mensagem, o que mantém o campo com altura estável antes e depois de um erro aparecer.
const RESERVA_DO_SLOT_AUXILIAR = '\u00A0';

// Declarado no escopo do módulo, e não dentro do render: o MUI compara `inputComponent` por
// referência, e uma referência nova remonta o input, perdendo foco e cursor no meio da digitação.
// As opções de máscara chegam por prop, então trocar a máscara em runtime não troca o componente.
const InputMascarado = forwardRef<HTMLInputElement, PropsDoInputMascarado>(function InputMascarado(
  { onChange, name, ...rest },
  ref,
) {
  // As opções de máscara da dependência formam uma união discriminada pelo tipo da máscara, e aqui
  // a máscara é `string | RegExp`: uma união não resolve para um membro concreto. A travessia é
  // este objeto único — nunca `any`, e a fronteira com quem usa o campo segue tipada por
  // `TextFieldProps`.
  const propsDaMascara = {
    ...rest,
    name,
    inputRef: ref,
    // A dependência não emite `change` do DOM: o valor combinado com a máscara chega em
    // `onAccept`, e é dele que sai o `onChange` esperado por quem consome o campo. O `target`
    // carrega `name` e `value`, o suficiente para handlers genéricos de formulário. O InputBase do
    // MUI sempre repassa o próprio `onChange` ao `inputComponent`, mesmo sem um no campo.
    onAccept: (valor: string) =>
      onChange({ target: { name, value: valor } } as unknown as ChangeEvent<HTMLInputElement>),
  } as ComponentProps<typeof IMaskInput>;

  return <IMaskInput {...propsDaMascara} />;
});

/** Campo de texto do design system: defaults do projeto, máscara opcional e altura estável. */
export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(function TextField(
  {
    variant = 'outlined',
    size = 'small',
    mask,
    unmask = true,
    transformChar,
    readOnly,
    reserveHelperTextSpace = true,
    helperText,
    FormHelperTextProps,
    InputProps,
    inputProps,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const temTextoAuxiliar = Boolean(helperText);
  const temSlotDeReserva = !temTextoAuxiliar && reserveHelperTextSpace;

  const propsDoInput = {
    // O MUI deriva `aria-describedby` da presença do slot auxiliar, que existe mesmo sem mensagem
    // quando reserva altura. Sem isso, o leitor de tela seria apontado para uma descrição vazia.
    // Vem antes do repasse de `InputProps`, para que um `aria-describedby` informado pelo
    // consumidor continue vencendo.
    ...(temSlotDeReserva ? { 'aria-describedby': undefined } : null),
    ...InputProps,
    // Incluído sempre, `readOnly: undefined` sobreviveria ao spread de `InputProps` e apagaria um
    // `InputProps={{ readOnly: true }}` informado pelo consumidor.
    ...(readOnly === undefined ? null : { readOnly }),
    ...(mask ? { inputComponent: InputMascarado } : null),
    inputProps: {
      // O `aria-label` de topo do MUI cai no FormControl, não no input: sem esse repasse, o campo
      // de busca sem rótulo visível ficaria anônimo para leitor de tela.
      ...(ariaLabel === undefined ? null : { 'aria-label': ariaLabel }),
      ...inputProps,
      ...InputProps?.inputProps,
      // A prop dedicada vence um `mask` avulso vindo de `inputProps`, que continua sendo o escape
      // hatch para o restante das opções da dependência (`definitions`, `blocks`, `lazy`, …).
      ...(mask ? { mask, unmask, prepareChar: transformChar } : null),
    },
  };

  // Objeto único: o tipo de props do MUI é uma união discriminada por `variant`, e o TypeScript não
  // correlaciona uma variante conhecida só em runtime com o `InputProps` do membro correspondente.
  const propsDoCampo = {
    variant,
    size,
    ...rest,
    helperText: temSlotDeReserva ? RESERVA_DO_SLOT_AUXILIAR : helperText,
    FormHelperTextProps: {
      // Sem mensagem de verdade, o slot existe só para reservar espaço: `aria-hidden` impede que o
      // leitor de tela anuncie um parágrafo em branco. O que o consumidor passou prevalece.
      ...(temSlotDeReserva ? { 'aria-hidden': true } : null),
      ...FormHelperTextProps,
    },
    InputProps: propsDoInput,
  } as MuiTextFieldProps;

  return <MuiTextField ref={ref} {...propsDoCampo} />;
});
